/**
 * Turning a photo somebody picked on their phone into something that can live in a database row.
 *
 * THE SIZE PROBLEM IS THE WHOLE PROBLEM. A picture off an iPhone camera roll is three to six
 * megabytes; the badge it ends up in is twenty-eight pixels across. Uploaded untouched it would
 * be refused by Firestore's document limit — silently, at the moment somebody was trying to be
 * friendly with the app — so nothing here ever passes the chosen file through. It is decoded,
 * cropped square, drawn down to 96px and re-encoded, and only that comes out.
 *
 * The maths is separated from the canvas so it can be tested without one. Everything below
 * `squareCrop` needs a browser; `squareCrop` itself is the part that used to be wrong.
 */

/** What the badge is drawn at, doubled for a retina screen and then some. */
export const AVATAR_PX = 96;

/** Comfortably under the rules file's ceiling, which is itself far under Firestore's. */
export const MAX_BYTES = 60_000;

export interface Crop { sx: number; sy: number; size: number }

/**
 * The largest centred square inside an image.
 *
 * Centred and not top-left, which sounds like a detail and is the difference between a portrait
 * and a picture of somebody's shoulder: phone photos are 3:4 tall, so a top-left square of a
 * head-and-shoulders shot is the head, and a top-left square of a full-length one is the sky.
 * Centring is not right for every photo either — nothing is, without a cropper — but it is right
 * far more often, and the person can always crop before choosing.
 */
export function squareCrop(width: number, height: number): Crop {
  const size = Math.min(width, height);
  return {
    sx: Math.round((width - size) / 2),
    sy: Math.round((height - size) / 2),
    size,
  };
}

/**
 * How hard to squeeze.
 *
 * JPEG quality is not linear in bytes and a photo of a face compresses very differently from a
 * screenshot of a logo, so rather than picking a number and hoping, this steps down until the
 * result fits. Three tries is enough at 96px — the first almost always lands — and the floor is
 * high enough that the worst case still looks like a person.
 */
export const QUALITY_STEPS = [0.82, 0.7, 0.55] as const;

/**
 * Decode, crop, resize, encode. Returns a `data:image/jpeg;base64,...` URL.
 *
 * Rejects rather than returning something unusable: a file that is not an image, a file the
 * browser cannot decode, or — after every quality step — a result still too large. The caller
 * shows the message; none of them are conditions the person can do anything about except choose
 * a different picture, so they all say that.
 */
export async function toAvatar(file: Blob, px = AVATAR_PX): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new Error('That is not an image. Choose a photo.');
  }
  const bitmap = await decode(file);
  const crop = squareCrop(bitmap.width, bitmap.height);
  try {
    return encodeCrop(bitmap, crop, px);
  } finally {
    close(bitmap);
  }
}

/**
 * The same pipeline from a crop the person chose (Pass 15's Change photo stage), rather than the
 * centred default. The crop is in source pixels; anything outside the image is drawn as nothing,
 * which is what a photo zoomed out inside the circle looks like on the stage too.
 */
export function encodeCrop(bitmap: CanvasImageSource & Decoded, crop: Crop, px = AVATAR_PX): string {
  const canvas = document.createElement('canvas');
  canvas.width = px;
  canvas.height = px;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser will not resize the picture. Try another.');
  // Downscaling by 20x in one step is what makes a resized photo look like a thumbnail from 2004.
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, crop.sx, crop.sy, crop.size, crop.size, 0, 0, px, px);

  for (const q of QUALITY_STEPS) {
    const url = canvas.toDataURL('image/jpeg', q);
    if (byteLength(url) <= MAX_BYTES) return url;
  }
  throw new Error('That picture will not compress small enough. Try a simpler one.');
}

/** Decode a file to something a canvas can draw, with its size. Exported for the crop stage. */
export async function decodeImage(file: Blob): Promise<CanvasImageSource & Decoded> {
  if (!file.type.startsWith('image/')) {
    throw new Error('That is not an image. Choose a photo.');
  }
  return decode(file);
}

/** Roughly what the string will cost in the database. Base64 is 4 characters per 3 bytes. */
export function byteLength(dataUrl: string): number {
  const comma = dataUrl.indexOf(',');
  const b64 = comma === -1 ? dataUrl : dataUrl.slice(comma + 1);
  const padding = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0;
  return Math.floor((b64.length * 3) / 4) - padding;
}

export interface Decoded { width: number; height: number }

/** `createImageBitmap` where it exists, an `<img>` where it does not. Safari has both now. */
async function decode(file: Blob): Promise<CanvasImageSource & Decoded> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch {
      /* Fall through — some Safari versions refuse HEIC here but decode it in an <img>. */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((res, rej) => {
      const img = new Image();
      img.onload = () => res(img);
      img.onerror = () => rej(new Error('That picture could not be opened. Try a JPEG or PNG.'));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function close(b: CanvasImageSource): void {
  if (typeof ImageBitmap !== 'undefined' && b instanceof ImageBitmap) b.close();
}
